import type { FC } from 'react';

import type { LegalDocumentContent } from '@/types/legal.types';

interface LegalDocumentProps {
  content: LegalDocumentContent;
  lastUpdated: string;
}

const LegalDocument: FC<LegalDocumentProps> = ({ content, lastUpdated }) => (
  <article className="bg-white">
    <div className="mx-auto w-full max-w-6xl px-5 py-16 md:px-8 md:py-20">
      <header className="max-w-3xl">
        <p className="text-xs font-semibold tracking-[0.14em] text-brand-600 uppercase">Legal</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold text-[var(--color-ink)] md:text-5xl">
          {content.title}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-[var(--color-ink-muted)]">
          {content.summary}
        </p>
        <p className="mt-4 text-sm text-[var(--color-ink-soft)]">Last updated: {lastUpdated}</p>
      </header>

      <div className="mt-12 grid gap-12 lg:grid-cols-[240px_1fr]">
        <nav
          aria-label="On this page"
          className="h-max lg:sticky lg:top-24 lg:rounded-2xl lg:border lg:border-[var(--color-divider)] lg:bg-[var(--color-surface-muted)] lg:p-5"
        >
          <h2 className="text-xs font-semibold tracking-[0.14em] text-[var(--color-ink-soft)] uppercase">
            On this page
          </h2>
          <ul className="mt-4 flex flex-col gap-2">
            {content.sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="text-sm leading-snug text-[var(--color-ink-muted)] transition-colors hover:text-brand-600"
                >
                  {section.heading}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex max-w-3xl flex-col gap-10">
          {content.sections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-24">
              <h2 className="font-display text-xl font-bold text-[var(--color-ink)] md:text-2xl">
                {section.heading}
              </h2>

              {section.paragraphs?.map((paragraph) => (
                <p
                  key={paragraph}
                  className="mt-4 text-[15px] leading-relaxed text-[var(--color-ink-muted)]"
                >
                  {paragraph}
                </p>
              ))}

              {section.bullets ? (
                <ul className="mt-4 flex flex-col gap-3">
                  {section.bullets.map((bullet) => (
                    <li
                      key={bullet}
                      className="relative pl-5 text-[15px] leading-relaxed text-[var(--color-ink-muted)] before:absolute before:top-[10px] before:left-0 before:h-1.5 before:w-1.5 before:rounded-full before:bg-brand-400"
                    >
                      {bullet}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
        </div>
      </div>
    </div>
  </article>
);

export default LegalDocument;
